<script setup lang="ts">
import { computed, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import { isSupportWindow } from '@/utils'
import { licenseModule } from '@/store/license/module'
import { examModule } from '@/store/exam/module'
import { organizationModule } from '@/store/organization/module'
import { analyticsModule } from '@/store/analytics/module'
import PurchaseSeatsModal from '@/components/SeatsModal/PurchaseSeatsModal.vue'

const { Button: PocketButton } = UIKit

const showPurchaseSeatsModal = ref(false)

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const currentOrgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const currentExamMetadata = computed(() => {
    return examModule.getters.getCurrentExamMetadata()
})

const areAllOrgExamsFree = computed(() => {
    return examModule.getters.getAreAllOrgExamsFree()
})

const showPurchaseSeatsBanner = computed(() => {
    return !areAllOrgExamsFree.value
        && currentOrgLicenses.value.length === 0
        && organizationModule.getters.getHasSelfServicePaymentAccess()
})

const currentExamNumQuestions = computed(() => {
    return currentExamMetadata.value
        ? currentExamMetadata.value.itemCount - currentExamMetadata.value.archivedCount
        : 0
})

const emit = defineEmits<{
    'close': [value: boolean]
}>()

const emitClose = () => {
    emit('close', true)
    return true
}

const showPurchaseSeatsSidePanel = () => {
    showPurchaseSeatsModal.value = true

    if (currentOrganization.value) {
        // Fire amplitude event
        analyticsModule.actions.amplitudeTrack('Side Panel Opened', {
            sidePanelName: 'Purchase Seats',
            sidePanelPlacement: 'Content banner',
            organizationId: currentOrganization.value.objectId,
            organizationName: currentOrganization.value.name,
        })
    }
}

const showIntercom = () => {
    if (import.meta.env.VUE_APP_INTERCOM_APP_ID && !isSupportWindow()) {
        window.Intercom('showNewMessage')
        emitClose()
    }
}
</script>

<template>
    <div class="content-banner">
        <PurchaseSeatsModal
            v-if="showPurchaseSeatsModal"
            @close="showPurchaseSeatsModal = false"
        />
        <div
            v-if="!showPurchaseSeatsBanner"
            class="content-banner__feedback"
        >
            <div class="content-banner__text">
                Our content is written by subject matter experts. If you
                see opportunities for improvement, send us a message.
            </div>
            <PocketButton
                class="content-banner__button"
                type="tertiary"
                @click="showIntercom"
            >
                Give Feedback
            </PocketButton>
        </div>
        <div
            v-else
        >
            <div class="content-banner__text">
                Preview some of our quiz questions below. Purchase seats for your students to unlock all
                {{ currentExamNumQuestions }} questions.
            </div>
            <PocketButton
                class="content-banner__button"
                type="tertiary-small"
                @click="showPurchaseSeatsSidePanel"
            >
                Purchase Seats
            </PocketButton>
        </div>
    </div>
</template>

<style lang="scss" scoped>


.content-banner {
    border: 1px solid;
    border-radius: 6px;
    border-color: rgba($brand-blue, 0.5);
    width: 386px;
    height: 95px;
    background-color: $barely-blue;
    position: absolute;
    right: 60px;
    top: 56px;

    &__text {
        font-size: 14px;
        line-height: 20px;
        padding: 14px 14px 8px 14px;
    }

    &__button {
        font-size: 14px;
        font-weight: 600;
        line-height: 17px;
        padding: 0 0 14px 14px;
    }
}
</style>
